import React, { useEffect, useState } from 'react';
import { BarChart2, Download, Play, AlertTriangle, RefreshCw } from 'lucide-react';
import AppShell from '../components/AppShell';
import { Button } from '../components/ui/button';
import axiosInstance from '../utils/axiosInstance';

const Backtest = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [running, setRunning] = useState(false);
  const [fetchLimit, setFetchLimit] = useState(100);
  const [lastOp, setLastOp] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('api/replai/backtest/summary/');
      setSummary(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const fetchClosed = async () => {
    setFetching(true);
    setLastOp(null);
    try {
      const { data } = await axiosInstance.post('api/replai/backtest/fetch/', {
        limit: Number(fetchLimit),
        scan_cap: Math.max(Number(fetchLimit) * 10, 500),
      });
      setLastOp({ kind: 'fetch', data });
      await load();
    } catch (e) {
      console.error(e);
      setLastOp({ kind: 'fetch', error: e.response?.data?.error || e.message });
    } finally {
      setFetching(false);
    }
  };

  const runBacktest = async () => {
    setRunning(true);
    setLastOp(null);
    try {
      const { data } = await axiosInstance.post('api/replai/backtest/run/', {});
      setLastOp({ kind: 'run', data });
      await load();
    } catch (e) {
      console.error(e);
      setLastOp({ kind: 'run', error: e.response?.data?.error || e.message });
    } finally {
      setRunning(false);
    }
  };

  const header = (
    <div className="flex items-center gap-2">
      <input
        type="number"
        min={10}
        max={2000}
        value={fetchLimit}
        onChange={(e) => setFetchLimit(e.target.value)}
        className="h-10 w-24 rounded-xl border border-border bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
        title="How many closed tickets to fetch"
      />
      <Button onClick={fetchClosed} disabled={fetching} variant="outline" className="gap-2">
        <Download className={`h-4 w-4 ${fetching ? 'animate-pulse' : ''}`} />
        {fetching ? 'Fetching…' : 'Fetch closed'}
      </Button>
      <Button onClick={runBacktest} disabled={running} className="gap-2">
        <Play className={`h-4 w-4 ${running ? 'animate-pulse' : ''}`} />
        {running ? 'Running…' : 'Run backtest'}
      </Button>
    </div>
  );

  return (
    <AppShell
      title="Backtest"
      subtitle="Shadow-dispatch historical closed tickets and score Replai against the agent's actual reply."
      headerRight={header}
    >
      {lastOp && (
        <div className={`mb-4 rounded-xl border px-4 py-3 text-sm ${
          lastOp.error ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-emerald-200 bg-emerald-50 text-emerald-800'
        }`}>
          {lastOp.error ? (
            <><AlertTriangle className="h-4 w-4 inline mr-2" />{lastOp.error}</>
          ) : (
            <>
              <span className="font-medium capitalize">{lastOp.kind}</span>: <code className="text-xs">{JSON.stringify(lastOp.data)}</code>
            </>
          )}
        </div>
      )}

      {!summary && loading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {summary && (
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <Stat label="Eval tickets" value={summary.totals.tickets} tone="neutral" />
            <Stat label="Runs" value={summary.totals.runs} tone="neutral" />
            <Stat label="Judged" value={summary.totals.judged} tone="success" />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <section className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-border/70 flex items-center gap-2">
                <BarChart2 className="h-4 w-4 text-[hsl(var(--primary))]" />
                <h3 className="text-sm font-medium">Per category</h3>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--secondary))] text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 text-left">Category</th>
                    <th className="px-4 py-2 text-right">N</th>
                    <th className="px-4 py-2 text-right">Avg score</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/70">
                  {summary.by_category.map((r) => (
                    <tr key={r.classifier_category}>
                      <td className="px-4 py-2 font-mono text-xs">{r.classifier_category}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{r.n}</td>
                      <td className={`px-4 py-2 text-right tabular-nums font-semibold ${
                        r.avg_score >= 4 ? 'text-emerald-700' : r.avg_score >= 3 ? 'text-amber-700' : 'text-rose-700'
                      }`}>
                        {r.avg_score?.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                  {summary.by_category.length === 0 && (
                    <tr><td colSpan={3} className="px-4 py-6 text-center text-muted-foreground italic">No judged runs yet</td></tr>
                  )}
                </tbody>
              </table>
            </section>

            <section className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-border/70 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <h3 className="text-sm font-medium">Failure modes</h3>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--secondary))] text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 text-left">Mode</th>
                    <th className="px-4 py-2 text-right">N</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/70">
                  {summary.by_failure_mode.map((r) => (
                    <tr key={r.judge_failure_mode}>
                      <td className="px-4 py-2 font-mono text-xs">{r.judge_failure_mode}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{r.n}</td>
                    </tr>
                  ))}
                  {summary.by_failure_mode.length === 0 && (
                    <tr><td colSpan={2} className="px-4 py-6 text-center text-muted-foreground italic">No failure data yet</td></tr>
                  )}
                </tbody>
              </table>
            </section>
          </div>

          <section className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-border/70 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-rose-600" />
              <h3 className="text-sm font-medium">Worst 20 cases (score ≤ 2)</h3>
            </div>
            <ul className="divide-y divide-border/70 max-h-[60vh] overflow-y-auto">
              {summary.bad_cases.length === 0 && (
                <li className="px-5 py-6 text-center text-sm text-muted-foreground italic">
                  No bad cases yet - run a backtest first.
                </li>
              )}
              {summary.bad_cases.map((c) => (
                <li key={c.id} className="px-5 py-4 hover:bg-[hsl(var(--secondary)/0.4)]">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <span className="pill text-[0.65rem] bg-rose-50 text-rose-700">
                      {c.judge_score?.toFixed(1)}/5
                    </span>
                    <span className="pill text-[0.65rem]">{c.classifier_category}</span>
                    {c.judge_failure_mode && (
                      <span className="pill text-[0.65rem] bg-amber-50 text-amber-700">
                        {c.judge_failure_mode}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">#{c.ticket__gorgias_id}</span>
                    <span className="text-xs text-muted-foreground">closed by {c.ticket__closed_by_name}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-1">
                    <span className="font-semibold text-foreground">Judge:</span> {c.judge_reason}
                  </p>
                  <p className="text-xs text-foreground font-medium mb-1">{c.ticket__subject}</p>
                  <div className="grid grid-cols-3 gap-3 mt-2">
                    <div className="rounded-lg bg-[hsl(var(--secondary))] p-2">
                      <p className="text-[0.6rem] uppercase text-muted-foreground mb-1">Customer</p>
                      <p className="text-xs whitespace-pre-wrap leading-snug line-clamp-5">{c.ticket__customer_message}</p>
                    </div>
                    <div className="rounded-lg bg-emerald-50/50 p-2">
                      <p className="text-[0.6rem] uppercase text-muted-foreground mb-1">Agent (ground truth)</p>
                      <p className="text-xs whitespace-pre-wrap leading-snug line-clamp-5">{c.ticket__agent_reply}</p>
                    </div>
                    <div className="rounded-lg bg-rose-50/50 p-2">
                      <p className="text-[0.6rem] uppercase text-muted-foreground mb-1">Replai draft</p>
                      <p className="text-xs whitespace-pre-wrap leading-snug line-clamp-5">{c.replai_draft || '(empty)'}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>
      )}
    </AppShell>
  );
};

const Stat = ({ label, value, tone }) => {
  const map = {
    neutral: 'bg-white border-border',
    success: 'bg-emerald-50/60 border-emerald-200/70',
  };
  return (
    <div className={`rounded-2xl border ${map[tone]} p-4`}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="font-display text-3xl font-semibold mt-1 tabular-nums">{value}</p>
    </div>
  );
};

export default Backtest;
